import { createFileRoute, notFound } from "@tanstack/react-router";
import { z } from "zod";

import { BranchBoard } from "@/presentation/components/roadmaps/branch-board";
import { CreateFeatureDialog } from "@/presentation/components/roadmaps/create-feature-dialog";
import { DropDialog } from "@/presentation/components/roadmaps/drop-dialog";
import { EditVerdictDialog } from "@/presentation/components/roadmaps/edit-verdict-dialog";
import { FeatureInfoDialog } from "@/presentation/components/roadmaps/feature-info-dialog";
import { VerdictLegend } from "@/presentation/components/roadmaps/verdict-legend";
import { PageHeader } from "@/presentation/components/shell/page-header";
import { DOMAIN_LABEL, DOMAINS, isDomain, type Domain } from "@/presentation/data/domains";
import {
  isRoadmapBranch,
  ROADMAP_COLOR,
  ROADMAP_LABEL,
  ROADMAP_TAGLINE,
} from "@/presentation/data/roadmaps";
import { useBranchBoardPage } from "@/presentation/hooks/use-branch-board-page";

const branchSearchSchema = z.object({
  domain: z.string().optional(),
});

export const Route = createFileRoute("/_app/roadmaps/$branch")({
  validateSearch: branchSearchSchema,
  loader: ({ params }) => {
    if (!isRoadmapBranch(params.branch)) throw notFound();
    return null;
  },
  component: BranchPage,
});

function BranchPage() {
  const { branch: rawBranch } = Route.useParams();
  const { domain: domainParam } = Route.useSearch();
  const branch = isRoadmapBranch(rawBranch) ? rawBranch : "all-features";
  const domainFilter: Domain | null = domainParam && isDomain(domainParam) ? domainParam : null;
  const page = useBranchBoardPage(branch, domainFilter);
  if (!isRoadmapBranch(rawBranch)) return null;
  const accent = ROADMAP_COLOR[branch];

  return (
    <div className="flex flex-col">
      <PageHeader
        eyebrow={`Roadmap · ${ROADMAP_LABEL[branch]}`}
        title={ROADMAP_TAGLINE[branch]}
        summary={
          branch === "all-features"
            ? "Drag a feature from the stack onto MVP or Non-MVP. MVP features automatically appear on the MVP board."
            : "Drag a feature from the stack onto off-the-shelf, build-on-top, or from-scratch. Only MVP-classified features show up here."
        }
      />
      <div className="animate-page-enter flex flex-col gap-4 px-6 py-6">
        <BoardToolbar branch={branch} domainFilter={domainFilter} accent={accent} page={page} />
        <VerdictLegend branch={branch} />
        <BranchBoard branch={branch} page={page} />
      </div>
      <DropDialog
        pending={page.pendingDrop}
        onConfirm={page.confirmDrop}
        onCancel={page.cancelDrop}
      />
      <FeatureInfoDialog feature={page.infoFeature} onClose={page.closeInfo} />
      <EditVerdictDialog
        branch={branch}
        feature={page.editingFeature}
        currentVerdict={
          page.editingFeature
            ? (page.verdictsByFeature.get(page.editingFeature.id)?.verdict ?? null)
            : null
        }
        currentNotes={page.editingFeature?.notes ?? null}
        onSave={page.saveEdit}
        onRemove={page.removeFromBoard}
        onClose={page.closeEdit}
      />
      <CreateFeatureDialog
        open={page.createOpen}
        branch={branch}
        isSubmitting={page.isCreating}
        onSubmit={page.submitCreate}
        onClose={page.closeCreate}
      />
    </div>
  );
}

function BoardToolbar({
  branch,
  domainFilter,
  accent,
  page,
}: {
  branch: ReturnType<typeof Route.useParams>["branch"];
  domainFilter: Domain | null;
  accent: string;
  page: ReturnType<typeof useBranchBoardPage>;
}) {
  const navigate = Route.useNavigate();
  const verdictedCount = page.verdictsByFeature.size;
  const total = page.totalFeatures;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
      <div className="flex items-center gap-3">
        <span
          className="inline-flex size-2.5 rounded-full"
          style={{ background: accent }}
          aria-hidden
        />
        <p className="font-data text-foreground/85 text-[12px] tracking-tight">
          <span className="text-foreground font-semibold tabular-nums">{verdictedCount}</span>
          <span className="text-muted-foreground"> / {total} features verdicted</span>
        </p>
      </div>
      <DomainFilter
        active={domainFilter}
        onChange={(next) => {
          void navigate({
            search: (prev) => ({ ...prev, domain: next ?? undefined }),
            params: { branch },
            replace: true,
          });
        }}
      />
    </div>
  );
}

function DomainFilter({
  active,
  onChange,
}: {
  active: Domain | null;
  onChange: (next: Domain | null) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      <button
        type="button"
        onClick={() => onChange(null)}
        data-active={active === null}
        className="font-data rounded-md border border-[oklch(0.91_0.008_240)] bg-white px-2.5 py-1 text-[11px] tracking-tight transition-colors data-[active=true]:border-[oklch(0.55_0.17_190)] data-[active=true]:bg-[oklch(0.55_0.17_190_/_0.1)] data-[active=true]:text-[oklch(0.45_0.18_195)]"
      >
        All domains
      </button>
      {DOMAINS.map((d) => (
        <button
          key={d}
          type="button"
          onClick={() => onChange(d)}
          data-active={active === d}
          className="font-data rounded-md border border-[oklch(0.91_0.008_240)] bg-white px-2.5 py-1 text-[11px] tracking-tight transition-colors data-[active=true]:border-[oklch(0.55_0.17_190)] data-[active=true]:bg-[oklch(0.55_0.17_190_/_0.1)] data-[active=true]:text-[oklch(0.45_0.18_195)]"
        >
          {DOMAIN_LABEL[d]}
        </button>
      ))}
    </div>
  );
}
